import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// public
import isEmpty from 'lodash/isEmpty';
import { useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useIntl } from 'react-intl';
import { usePopOver } from '@xingternal/pop-over';
import { BREAKPOINTS, useMediaListener } from '@xingternal/util';
import { useViewerData, useFeature } from '@xingternal/hub';
import fetchUserFlag from './data/fetch-user-flag';
import { FlagPopOver } from './flag-pop-over';
import { FlagBottomSheet } from './flag-bottom-sheet';
import { Styled } from './styles';
import { trackFlagClick, trackCtaUrl } from './tracking';
const getFlag = (displayFlag, size, showBasicFlag, props) => {
    switch (displayFlag) {
        case 'AMBASSADOR': {
            return (_jsx(Styled.FlagWithPointer, { size: size, variant: "ambassador", ...props }));
        }
        case 'BASIC': {
            return (showBasicFlag && (_jsx(Styled.FlagWithPointer, { size: size, variant: "basic", ...props })));
        }
        case 'INSIDER': {
            return (_jsx(Styled.FlagWithPointer, { size: size, variant: "insider", ...props }));
        }
        case 'MODERATOR': {
            return (_jsx(Styled.FlagWithPointer, { size: size, variant: "moderator", ...props }));
        }
        case 'PREMIUM': {
            return (_jsx(Styled.FlagWithPointer, { size: size, variant: "premium", ...props }));
        }
        case 'PRO_COACH': {
            return (_jsx(Styled.FlagWithPointer, { size: size, variant: "proCoach", ...props }));
        }
        case 'PRO_TRAINER': {
            return (_jsx(Styled.FlagWithPointer, { size: size, variant: "proTrainer", ...props }));
        }
    }
};
export const UserFlag = ({ displayFlag, showBasicFlag = false, size = 'medium', userId, ...props }) => {
    var _a, _b, _c, _d;
    const apolloClient = useApolloClient();
    const intl = useIntl();
    const user = (_a = useViewerData().data) === null || _a === void 0 ? void 0 : _a.user;
    const viewerUserId = (user === null || user === void 0 ? void 0 : user.id) || 0;
    const viewerDisplayFlag = ((_b = user === null || user === void 0 ? void 0 : user.userFlags) === null || _b === void 0 ? void 0 : _b.displayFlag) ||
        'BASIC';
    const isPremium = useFeature('isPremium', false);
    const popOver = usePopOver();
    const breakpoint = useMediaListener();
    const isHandheld = breakpoint === BREAKPOINTS.handheld;
    const [showBottomSheet, setShowBottomSheet] = useState(false);
    const [userFlagInfo, setUserFlagInfo] = useState();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const displayPopOver = !isHandheld;
    const hasUserFlagInfo = !isEmpty(userFlagInfo);
    // all the data should be provided for the click feature to work
    const hasClickableFeature = userId && displayFlag;
    // load user flag data, will load just once for each flag
    const loadUserFlagInfo = async () => {
        if (hasClickableFeature && !hasUserFlagInfo) {
            setLoading(true);
            setError(false);
            trackFlagClick({
                viewerId: viewerUserId,
                otherId: userId || null,
                displayFlag: displayFlag,
            });
            const { fetchedInfo, error } = await fetchUserFlag({
                apolloClient,
                viewerUserId,
                viewerDisplayFlag,
                otherUserId: userId,
                otherDisplayFlag: displayFlag,
            });
            setUserFlagInfo(fetchedInfo);
            setError(error);
            setLoading(false);
        }
    };
    const commonFlagProps = displayPopOver
        ? {
            onClick: async (e) => {
                e.stopPropagation();
                e.preventDefault();
                popOver.handleShow();
                loadUserFlagInfo();
            },
            innerRef: popOver.triggerRef,
        }
        : {
            onClick: async (e) => {
                e.stopPropagation();
                e.preventDefault();
                setShowBottomSheet(true);
                loadUserFlagInfo();
            },
        };
    const isViewerPremium = viewerDisplayFlag === 'PREMIUM';
    const hasPremiumTheme = isViewerPremium
        ? displayFlag === 'PREMIUM'
        : displayFlag === 'PREMIUM' || displayFlag === 'BASIC';
    const userFlagProps = {
        headerCopy: (userFlagInfo === null || userFlagInfo === void 0 ? void 0 : userFlagInfo.title) || '',
        bodyCopy: (userFlagInfo === null || userFlagInfo === void 0 ? void 0 : userFlagInfo.body) || [],
        ctaCopy: ((_c = userFlagInfo === null || userFlagInfo === void 0 ? void 0 : userFlagInfo.action) === null || _c === void 0 ? void 0 : _c.text) || null,
        ctaUrl: trackCtaUrl({
            url: ((_d = userFlagInfo === null || userFlagInfo === void 0 ? void 0 : userFlagInfo.action) === null || _d === void 0 ? void 0 : _d.url) || null,
            isPremium: isPremium,
            displayFlag: displayFlag,
        }),
        premiumTheme: hasPremiumTheme,
    };
    return (_jsxs(Styled.FlagWrapper, { children: [_jsx(Styled.UserFlagButton, { "aria-label": intl.formatMessage({
                    id: 'USER_FLAG_BUTTON_ARIA_LABEL',
                }, { type: displayFlag }), ...commonFlagProps, children: getFlag(displayFlag, size, showBasicFlag, { ...props }) }), hasClickableFeature && displayPopOver && (_jsx(FlagPopOver, { onCloseClick: (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    popOver.handleHide();
                }, onOutsideClick: popOver.handleHide, triggerRef: popOver.triggerRef, show: popOver.show, isInFlow: true, userFlagProps: userFlagProps, loading: loading, error: error, success: hasUserFlagInfo })), hasClickableFeature && showBottomSheet && (_jsx(FlagBottomSheet, { dimmerTitle: "user-flag-close", showBottomSheet: showBottomSheet, setShowBottomSheet: setShowBottomSheet, userFlagProps: userFlagProps, loading: loading, error: error, success: hasUserFlagInfo }))] }));
};
//# sourceMappingURL=user-flag.js.map